import { z } from 'zod';
import { pipe } from 'fp-ts/function';
import { array, record } from 'fp-ts';
import { FormState } from 'react-hook-form';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { getBase64, Validation } from '@mews/framework';
import { ProductCategoryUpdateParameters } from '@mews-commander/data-access';
import { useCurrentService } from '../../queries/useCurrentService';

export const useProductCategoryFormSchema = () => {
    const { defaultLanguageCode } = useCurrentEnterprise();
    return z.object({
        parentProductCategoryId: z.string().nullable().default(null),
        names: Validation.Localized.Required(defaultLanguageCode).default({ [defaultLanguageCode]: '' }),
        shortNames: Validation.Localized.Optional().default(null),
        descriptions: Validation.Localized.Optional().default(null),
        ordering: Validation.Number.MaxInteger.default(0).nullish(),
        image: Validation.File.Image({
            sizeLimit: {
                bytes: 5 * 1024 * 1024,
                text: '5MB',
            },
        }).nullish(),
    });
};

export type ProductCategoryFormValues = z.infer<ReturnType<typeof useProductCategoryFormSchema>>;

export const useToProductCategoryUpdateParameters = ({ productCategoryId }: { productCategoryId: string }) => {
    const productCategoryFormSchema = useProductCategoryFormSchema();
    const { id } = useCurrentService();

    return async (
        formValues: ProductCategoryFormValues,
        dirtyFields: FormState<ProductCategoryFormValues>['dirtyFields']
    ) =>
        productCategoryFormSchema
            .transform<ProductCategoryUpdateParameters>(async ({ image, descriptions, shortNames, ...others }) => {
                const updateValues = pipe(
                    {
                        ...others,
                        ...(record.isEmpty(shortNames) ? {} : { shortNames }),
                        ...(record.isEmpty(descriptions) ? {} : { descriptions }),
                        image:
                            image && image[0]
                                ? {
                                      name: image[0].name,
                                      type: image[0].type,
                                      data: await getBase64(image[0]),
                                  }
                                : null,
                    },
                    Object.entries,
                    array.map(([parameter, value]) => [parameter, { value }]),
                    array.filter(([parameter]) => (parameter === 'image' ? !!dirtyFields.image : true)),
                    Object.fromEntries
                );

                return {
                    ...updateValues,
                    productCategoryId,
                    serviceId: id,
                };
            })
            .parseAsync(formValues);
};
